import { Component, signal } from '@angular/core';
import { SetupWizardLessonForm } from '../../../../../forms/setup-wizard/setup-wizard-lesson-form/setup-wizard-lesson-form';
import { SetupWizardLessonsTable } from './setup-wizard-lessons-table/setup-wizard-lessons-table';
import { SetupWizard } from '../../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	SetupWizardPayloadObjectKeys,
	SetupWizardLesson,
	SetupWizardLessonFormPayload,
	SetupWizardActivityFormPayload,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardUploadCsvButton } from '../../setup-wizard-upload-csv-button/setup-wizard-upload-csv-button';
import { SetupWizardDownloadCsvButton } from '../../setup-wizard-download-csv-button/setup-wizard-download-csv-button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { SetupWizardActivityForm } from '../../../../../forms/setup-wizard/setup-wizard-activity-form/setup-wizard-activity-form';

@Component({
	selector: 'app-setup-wizard-lesson-editor',
	imports: [
		SetupWizardLessonForm,
		SetupWizardLessonsTable,
		NzButtonModule,
		SetupWizardUploadCsvButton,
		SetupWizardDownloadCsvButton,
		NzTabsModule,
		NzButtonModule,
		NzTooltipModule,
		SetupWizardActivityForm,
	],
	standalone: true,
	templateUrl: './setup-wizard-lesson-editor.html',
	styleUrl: './setup-wizard-lesson-editor.scss',
})
export class SetupWizardLessonEditor {
	public edit = signal<SetupWizardLesson | null>(null);
	public showForm = signal<boolean>(false);
	public loading = signal<boolean>(false);
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;

	constructor(
		private route: ActivatedRoute,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	public onFormSubmit(payload: SetupWizardLessonFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.lessons, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onActivityFormSubmit(payload: SetupWizardActivityFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.activities, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onFormClear() {
		this.showForm.set(false);
		this.edit.set(null);
	}

	public onAdd() {
		this.showForm.set(true);
		this.edit.set(null);
	}

	public onEdit($event: SetupWizardLesson) {
		if ($event.after || $event.before) {
			this.edit.set($event);
			this.showForm.set(true);
		}
	}

	public async onToggleDisable($event: SetupWizardLesson) {
		const beforeAfter = $event.after || $event.before;

		if (!beforeAfter) {
			throw new Error('No lesson data available to update');
		}

		await this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.lessons, {
			...beforeAfter,
			active: !beforeAfter.active,
		});
	}

	public async onReset($event: SetupWizardLesson) {
		if (!$event.after) {
			throw new Error('No lesson data available to reset');
		}

		await this.setupWizard.resetPayloadItem(SetupWizardPayloadObjectKeys.lessons, $event.after);
	}

	public async onRemove($event: SetupWizardLesson) {
		if (!$event.after) {
			throw new Error('No lesson data available to remove');
		}

		await this.setupWizard.removePayloadItem(SetupWizardPayloadObjectKeys.lessons, $event.after);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
